import axios from 'axios';

// Optimized API Configuration
const OPTIMIZED_API_URL = process.env.REACT_APP_OPTIMIZED_API_URL || 'https://shop.bailebelle.com/wp-json/bailebelle/v1';

// Create axios instance for optimized API
const optimizedApi = axios.create({
  baseURL: OPTIMIZED_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
});

// Request interceptor
optimizedApi.interceptors.request.use(
  (config) => {
    console.log(`Making optimized API request to: ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Optimized API request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
optimizedApi.interceptors.response.use(
  (response) => {
    console.log('Optimized API response received:', response.status, response.config.url);
    return response;
  },
  async (error) => {
    console.error('Optimized API Error Details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
    });
    
    // Retry logic for specific errors
    if (error.response?.status >= 500 && error.config && !error.config._retry) {
      error.config._retry = true;
      console.log('Retrying optimized API call...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      return optimizedApi(error.config);
    }
    
    return Promise.reject(error);
  }
);

// Cache for API responses
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

// Cache helper functions
const getCacheKey = (endpoint, params = {}) => {
  return `${endpoint}_${JSON.stringify(params)}`;
};

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Optimized API Methods
export const optimizedAPI = {
  /**
   * Get all home page slider products in a single request
   * Returns: new_arrivals, featured, best_selling, sale
   */
  getHomeSliderProducts: async (perPage = 8) => {
    const cacheKey = getCacheKey('/home-sliders', { per_page: perPage });
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log('Using cached home slider data');
      return cachedData;
    }
    
    try {
      const response = await optimizedApi.get('/home-sliders', {
        params: { per_page: perPage }
      });
      
      setCachedData(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching home slider products:', error);
      return {
        new_arrivals: [],
        featured: [],
        best_selling: [],
        sale: []
      };
    }
  },

  /**
   * Get category products with filtering and pagination
   */
  getCategoryProducts: async (params = {}) => {
    const {
      category,
      per_page = 12,
      page = 1,
      search,
      min_price,
      max_price,
      sort = 'date'
    } = params;

    const cacheKey = getCacheKey('/category-products', params);
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log('Using cached category products data');
      return cachedData;
    }
    
    try {
      const response = await optimizedApi.get('/category-products', {
        params: {
          category,
          per_page,
          page,
          search,
          min_price,
          max_price,
          sort
        }
      });
      
      setCachedData(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching category products:', error);
      return {
        products: [],
        total: 0,
        total_pages: 0,
        current_page: 1,
        per_page: 12,
        has_more: false
      };
    }
  },

  /**
   * Get collections products (all products with filtering)
   */
  getCollectionsProducts: async (params = {}) => {
    const {
      per_page = 8,
      page = 1,
      search,
      min_price,
      max_price,
      sort = 'date'
    } = params;

    const cacheKey = getCacheKey('/collections-products', params);
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log('Using cached collections products data');
      return cachedData;
    }
    
    try {
      const response = await optimizedApi.get('/collections-products', {
        params: {
          per_page,
          page,
          search,
          min_price,
          max_price,
          sort
        }
      });
      
      setCachedData(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching collections products:', error);
      return {
        products: [],
        total: 0,
        total_pages: 0,
        current_page: 1,
        per_page: 8,
        has_more: false
      };
    }
  },

  /**
   * Get category-specific collection products (e.g., /collections/dress)
   */
  getCategoryCollection: async (category, params = {}) => {
    const {
      per_page = 8,
      page = 1,
      search,
      min_price,
      max_price,
      sort = 'date',
      featured,
      on_sale
    } = params;

    const cacheKey = getCacheKey(`/collections/${category}`, params);
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log(`Using cached category collection data for ${category}`);
      return cachedData;
    }
    
    try {
      const response = await optimizedApi.get(`/collections/${category}`, {
        params: {
          per_page,
          page,
          search,
          min_price,
          max_price,
          sort,
          featured,
          on_sale
        }
      });
      
      setCachedData(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching category collection ${category}:`, error);
      return {
        products: [],
        total: 0,
        total_pages: 0,
        current_page: 1,
        per_page: 8,
        has_more: false,
        category_info: null,
        error: error.response?.data?.error || 'Category not found'
      };
    }
  },

  /**
   * Get product detail with related products
   */
  getProductDetail: async (productIdOrSlug, includeRelated = true, relatedPerPage = 4) => {
    const isNumeric = !isNaN(productIdOrSlug);
    const params = isNumeric 
      ? { id: productIdOrSlug }
      : { slug: productIdOrSlug };
    
    const cacheKey = getCacheKey('/product-detail', { 
      ...params,
      include_related: includeRelated,
      related_per_page: relatedPerPage
    });
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log('Using cached product detail data');
      return cachedData;
    }
    
    try {
      const response = await optimizedApi.get('/product-detail', {
        params: {
          ...params,
          include_related: includeRelated,
          related_per_page: relatedPerPage
        }
      });
      
      setCachedData(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching product detail:', error);
      return {
        product: null,
        related_products: []
      };
    }
  },

  /**
   * Search products
   */
  searchProducts: async (query, perPage = 8) => {
    if (!query || query.trim().length < 2) {
      return { products: [] };
    }

    const cacheKey = getCacheKey('/search-products', { query, per_page: perPage });
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log('Using cached search data');
      return cachedData;
    }
    
    try {
      const response = await optimizedApi.get('/search-products', {
        params: {
          query: query.trim(),
          per_page: perPage
        }
      });
      
      setCachedData(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Search error:', error);
      return { products: [] };
    }
  },

  /**
   * Get categories
   */
  getCategories: async () => {
    const cacheKey = getCacheKey('/categories');
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      console.log('Using cached categories data');
      return cachedData;
    }
    
    try {
      const response = await optimizedApi.get('/categories');
      setCachedData(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  },

  /**
   * Clear cache
   */
  clearCache: () => {
    cache.clear();
    console.log('Optimized API cache cleared');
  },

  /**
   * Clear specific cache entries
   */
  clearProductCache: (productId) => {
    const keysToDelete = [];
    for (const key of cache.keys()) {
      if (key.includes(`/product-detail`) && key.includes(`id:${productId}`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => cache.delete(key));
    console.log(`Cleared optimized API cache for product ${productId}`);
  }
};

export default optimizedAPI;
